/* Detecção da rede social a partir da URL do post.
 *
 * Roda no cliente, é síncrona e não faz requisição: o editor cola a URL e o
 * ícone certo aparece no mesmo instante. Isso é proposital — o ícone do card é
 * a única parte da automação que nunca pode depender de terceiro. Se o serviço
 * `@social-link-preview` estiver fora do ar, ou se a rede não publicar
 * metadados (Instagram, Facebook, LinkedIn), o card ainda sai com o ícone
 * correto e o editor preenche título e capa à mão.
 *
 * A tabela de domínios existe também no backend, em
 * `backend/src/ministerio/publico/federal/services/social_link_preview/providers.py`.
 * Mexeu aqui, mexa lá — não há como compartilhar o código entre TS e Python.
 */

import type { SocialGlyphToken } from '../../components/Icons/social';

export type SocialNetworkToken = SocialGlyphToken | 'unknown';

export const UNKNOWN_NETWORK: SocialNetworkToken = 'unknown';

/* Espelho de `DOMAIN_MAP` em `providers.py`. */
const DOMAIN_MAP: Record<string, SocialNetworkToken> = {
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'instagram.com': 'instagram',
  'facebook.com': 'facebook',
  'fb.watch': 'facebook',
  'wa.me': 'whatsapp',
  'whatsapp.com': 'whatsapp',
  'linkedin.com': 'linkedin',
  'x.com': 'x',
  'twitter.com': 'x',
  'threads.net': 'threads',
  'threads.com': 'threads',
  'open.spotify.com': 'spotify',
  'tiktok.com': 'tiktok',
  'flickr.com': 'flickr',
  'flic.kr': 'flickr',
  'music.amazon.com': 'amazonmusic',
  'music.amazon.com.br': 'amazonmusic',
  'deezer.com': 'deezer',
  'deezer.page.link': 'deezer',
};

/**
 * Token da rede a partir do domínio da URL, ou `unknown`.
 *
 * Subdomínio não listado (`m.youtube.com`, `br.linkedin.com`) cai no domínio
 * registrável — enumerar toda variação regional e móvel seria manutenção sem
 * fim. Os sufixos são testados do mais longo para o mais curto para que
 * `music.amazon.com.br` não seja confundido com um eventual `amazon.com`.
 */
export const resolveSocialNetwork = (url?: string): SocialNetworkToken => {
  if (!url) return UNKNOWN_NETWORK;

  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    // URL malformada é rotina no editor (ainda está digitando), não erro.
    return UNKNOWN_NETWORK;
  }
  if (!host) return UNKNOWN_NETWORK;

  host = host.replace(/^www\./, '');
  if (DOMAIN_MAP[host]) return DOMAIN_MAP[host];

  const parts = host.split('.');
  for (let start = 1; start < parts.length - 1; start += 1) {
    const candidate = parts.slice(start).join('.');
    if (DOMAIN_MAP[candidate]) return DOMAIN_MAP[candidate];
  }
  return UNKNOWN_NETWORK;
};
